import { useEffect, useRef, useState } from 'react';
import { readRecruitVerificationLink } from '@/utils/recruitVerificationLink';
import { RecruitVerificationRequests } from '@/utils/recruitVerificationRequests';

interface Props { onVerified: (nmls: string) => Promise<void>; onCancel: () => void }

export function RecruitEmailVerification({ onVerified, onCancel }: Props) {
  const [emailLink] = useState(() => readRecruitVerificationLink());
  const [nmls, setNmls] = useState(emailLink?.nmls ?? '');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState(emailLink?.challenge ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retryAt, setRetryAt] = useState(() => emailLink ? Date.now() + 60000 : 0);
  const [now, setNow] = useState(Date.now());
  const request = useRef<AbortController | null>(null);
  const flow = useRef(new RecruitVerificationRequests(emailLink?.challenge));
  const nmlsInput = useRef<HTMLInputElement>(null);
  const codeInput = useRef<HTMLInputElement>(null);
  const seconds = Math.max(0, Math.ceil((retryAt - now) / 1000));
  useEffect(() => {
    if (emailLink) {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
  }, [emailLink]);
  useEffect(() => {
    const currentFlow = flow.current;
    if (emailLink) {
      currentFlow.remember(emailLink.challenge);
    }
    nmlsInput.current?.focus();
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(timer);
      request.current?.abort();
      request.current = null;
      currentFlow.clear();
    };
  }, [emailLink]);
  useEffect(() => {
    if (challenge && !busy) {
      codeInput.current?.focus();
    }
  }, [challenge, busy]);

  const run = async (action: 'start' | 'verify') => {
    if (request.current || (action === 'start' && Date.now() < retryAt)) {
      return;
    }
    const controller = new AbortController();
    request.current = controller;
    setBusy(true); setError('');
    const deadline = setTimeout(() => {
      if (request.current === controller) {
        controller.abort(); request.current = null; setBusy(false);
        setError('Verification timed out. Please try again.');
      }
    }, 30000);
    try {
      const nextChallenge = await flow.current.run('/.netlify/functions/recruit-auth', action, nmls, code, controller.signal);
      if (controller.signal.aborted || request.current !== controller) {
        return;
      }
      if (action === 'start') {
        setChallenge(nextChallenge ?? ''); setRetryAt(Date.now() + 60000); setNow(Date.now());
      } else {
        await onVerified(nmls);
      }
    } catch (failure) {
      if (!controller.signal.aborted) {
        setError(failure instanceof Error ? failure.message : 'Unable to complete verification.');
      }
    } finally {
      clearTimeout(deadline);
      if (request.current === controller && !controller.signal.aborted) {
        request.current = null;
        setBusy(false);
      }
    }
  };
  const cancel = () => {
    request.current?.abort(); request.current = null; flow.current.clear();
    setBusy(false); setCode(''); setChallenge(''); setError(''); setRetryAt(0);
    onCancel();
  };
  const inputStyle = { width: '100%', padding: '0.8rem', borderRadius: 8, border: '1px solid #bca675', color: '#123a2d', background: '#fff', fontSize: 16 };
  return <form onSubmit={event => {
    event.preventDefault(); void run(challenge ? 'verify' : 'start');
  }}
 style={{ color: '#123a2d', display: 'grid', gap: '1rem' }}>
    <label htmlFor="recruit-nmls">NMLS number
      <input ref={nmlsInput} id="recruit-nmls" inputMode="numeric" autoComplete="off" pattern="[0-9]{6,10}" maxLength={10}
        value={nmls} required disabled={busy || !!challenge} style={inputStyle}
        onChange={event => setNmls(event.target.value.replace(/\D/g, ''))} />
    </label>
    {challenge && <>
      <p role="status" style={{ fontSize: 14, lineHeight: 1.5 }}>If your NMLS is registered and ready, a code will be sent to your saved email. Your assigned recruiter is copied. The code expires in 10 minutes.</p>
      <label htmlFor="recruit-code">Six-digit email code
        <input ref={codeInput} id="recruit-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6}
          value={code} required disabled={busy} style={inputStyle} onChange={event => setCode(event.target.value.replace(/\D/g, ''))} />
      </label>
      <button type="button" className="min-h-11 rounded-lg border border-slate-300 px-4 py-2 disabled:opacity-50 focus-visible:outline focus-visible:outline-2" disabled={busy || seconds > 0} onClick={() => void run('start')}>
        {seconds > 0 ? `Resend in ${seconds}s` : 'Send a new code'}
      </button>
      <button type="button" className="min-h-11 rounded-lg border border-slate-300 px-4 py-2 disabled:opacity-50 focus-visible:outline focus-visible:outline-2" disabled={busy} onClick={() => {
        flow.current.clear(); setChallenge(''); setCode(''); setError(''); setRetryAt(0); setTimeout(() => nmlsInput.current?.focus(), 0);
      }}>Change NMLS</button>
    </>}
    {error && <p role="alert" style={{ color: '#b91c1c', lineHeight: 1.5 }}>{error}</p>}
    <p style={{ fontSize: 14, lineHeight: 1.5 }}>Need your saved email changed? Contact <a style={{ color: '#123a2d', textDecoration: 'underline' }} href="mailto:join@lendwisemtg.com">join@lendwisemtg.com</a>.</p>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
      <button type="button" className="min-h-11 rounded-lg border border-slate-300 px-4 py-2 focus-visible:outline focus-visible:outline-2" onClick={cancel}>Cancel</button>
      <button type="submit" className="min-h-11 rounded-lg bg-[#0E3A2B] text-white px-4 py-2 disabled:opacity-50 focus-visible:outline focus-visible:outline-2" disabled={busy || !/^\d{6,10}$/.test(nmls) || (!!challenge && !/^\d{6}$/.test(code))}>
        {busy ? 'Please wait…' : challenge ? 'Verify and continue' : 'Send email code'}
      </button>
    </div>
  </form>;
}
